// ══════════════════════════════════════════════════════
//  TrafficAnalytics.jsx — Plausible 风格的流量分析面板
//  数据来源：/admin/traffic（聚合 usage_events 表）
//  用途：替代 Plausible（试用到期），admin 可见
// ══════════════════════════════════════════════════════
import React, { useEffect, useState, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { Calendar, Link2 } from 'lucide-react';

const PROXY = process.env.REACT_APP_PROXY_URL || 'https://primary-lens-production.up.railway.app';

const C = {
  bg: '#f9fafb', surface: '#ffffff', border: '#e5e7eb',
  text: '#111827', muted: '#6b7280',
  accent: '#6366f1',  // Plausible-ish indigo
  green: '#10b981', red: '#ef4444',
  highlight: '#eef2ff',
};

const TIP = { contentStyle: { fontSize: 12, borderRadius: 8 }, itemStyle: { fontSize: 12 } };

// ── 时间范围预设（key 与后端对应）────────────────────────────
const RANGES = [
  { key: 'today',     label: 'Today',       hint: '今日（按小时）' },
  { key: 'yesterday', label: 'Yesterday',   hint: '昨日（按小时）' },
  { key: '24h',       label: 'Last 24 H',   hint: '过去 24 小时' },
  { key: '7d',        label: 'Last 7 D',    hint: '过去 7 天' },
  { key: '28d',       label: 'Last 28 D',   hint: '过去 28 天' },
  { key: '91d',       label: 'Last 91 D',   hint: '过去 91 天' },
];

// ── KPI 卡片（含 vs 上期对比）────────────────────────────────
function KpiCard({ label, value, deltaPct, format = (v) => v, highlighted = false, onClick }) {
  const dir = deltaPct == null ? null : (deltaPct >= 0 ? 'up' : 'down');
  // bounce rate 的 up 是坏事，其他指标 up 是好事 — 用 invertDelta 取反
  return (
    <button onClick={onClick} style={{
      background: highlighted ? C.highlight : C.surface,
      border: `1px solid ${highlighted ? C.accent : C.border}`,
      borderRadius: 10, padding: '14px 18px', flex: 1, minWidth: 130,
      cursor: onClick ? 'pointer' : 'default', textAlign: 'left',
      transition: 'background 0.15s, border-color 0.15s',
    }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 800, color: C.text, lineHeight: 1 }}>
          {format(value)}
        </span>
        {deltaPct != null && Number.isFinite(deltaPct) && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            color: dir === 'up' ? C.green : dir === 'down' ? C.red : C.muted,
          }}>
            {dir === 'up' ? '↗' : '↘'} {Math.abs(deltaPct)}%
          </span>
        )}
      </div>
    </button>
  );
}

// ── 维度面板（Plausible 风格的 list with progress bar）──────
function BreakdownPanel({ title, tabs, items, getName = (it) => it.name, getCount = (it) => it.count, getPct = (it) => it.pct, valueLabel = 'Visitors' }) {
  const [activeTab, setActiveTab] = useState(0);
  const data = Array.isArray(tabs) ? (tabs[activeTab]?.items || []) : items;
  const max = data.length > 0 ? Math.max(...data.map(getCount)) : 1;
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '14px 16px', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
        {Array.isArray(tabs) ? (
          <div style={{ display: 'flex', gap: 18 }}>
            {tabs.map((t, i) => (
              <button key={t.key} onClick={() => setActiveTab(i)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                color: i === activeTab ? C.text : C.muted,
                paddingBottom: 4, borderBottom: i === activeTab ? `2px solid ${C.text}` : '2px solid transparent',
              }}>{t.label}</button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.text }}>{title}</div>
        )}
        <div style={{ fontSize: 11, color: C.muted }}>{valueLabel}</div>
      </div>
      {/* List */}
      {data.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 12 }}>暂无数据</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.slice(0, 8).map((it, i) => {
            const name = getName(it);
            const count = getCount(it);
            const pct = getPct(it);
            const width = `${Math.max(2, (count / max) * 100)}%`;
            return (
              <div key={i} style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, padding: '8px 10px', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width, background: i === 0 ? '#dbeafe' : '#f3f4f6',
                  borderRadius: 5, zIndex: 0,
                }} />
                <span style={{ position: 'relative', zIndex: 1, fontSize: 12, color: C.text, fontWeight: i === 0 ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                <span style={{ position: 'relative', zIndex: 1, fontSize: 12, color: C.text, fontWeight: 600, minWidth: 30, textAlign: 'right' }}>{count}</span>
                <span style={{ position: 'relative', zIndex: 1, fontSize: 11, color: C.muted, minWidth: 40, textAlign: 'right' }}>{pct != null ? `${(pct * 100).toFixed(0)}%` : ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Date range dropdown ──────────────────────────────────────
function RangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = RANGES.find(r => r.key === value) || RANGES[3];
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: C.surface, border: `1px solid ${C.accent}`, color: C.text,
        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Calendar size={13} strokeWidth={2} /> {current.label} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 20,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
          minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        }}>
          {RANGES.map(r => (
            <button key={r.key} onClick={() => { onChange(r.key); setOpen(false); }} style={{
              display: 'flex', justifyContent: 'space-between', width: '100%',
              padding: '10px 14px', background: r.key === value ? C.highlight : 'transparent',
              border: 'none', cursor: 'pointer', fontSize: 12, color: C.text, textAlign: 'left',
              borderBottom: `1px solid ${C.border}`,
            }}>
              <span>{r.label}</span>
              <span style={{ color: C.muted, fontSize: 10 }}>{r.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 主组件 ────────────────────────────────────────────────────
export default function TrafficAnalytics({ userId, accessToken }) {
  const [range, setRange] = useState('7d');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metricFocus, setMetricFocus] = useState('uniqueVisitors'); // 时序图聚焦的指标

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true); setError(null);
    fetch(`${PROXY}/admin/traffic?uid=${userId}&range=${range}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [userId, range, accessToken]);
  useEffect(() => { load(); }, [load]);

  const k     = data?.kpis || {};
  const vsPrev = data?.kpisVsPrev || {};
  const series = data?.series || [];

  // 时序图：根据 metricFocus 选 dataKey
  const seriesData = series.map(s => ({
    t: s.t,
    label: data?.granularity === 'hour' ? s.t.slice(11) : s.t.slice(5),
    value: s[metricFocus === 'pageviews' ? 'pageviews' : 'visitors'],
  }));

  return (
    <div>
      {/* Header: domain + range */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: C.muted, display: 'flex' }}><Link2 size={13} strokeWidth={2} /></span>
          <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>primary-lens-production.up.railway.app</span>
          {loading && <span style={{ fontSize: 11, color: C.muted }}>· loading…</span>}
        </div>
        <RangePicker value={range} onChange={setRange} />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: `1px solid ${C.red}`, padding: '10px 14px', borderRadius: 8, color: C.red, fontSize: 12, marginBottom: 12 }}>
          Error: {error}
        </div>
      )}

      {/* KPI cards row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <KpiCard label="Unique Visitors" value={k.uniqueVisitors} deltaPct={vsPrev.uniqueVisitors} highlighted={metricFocus==='uniqueVisitors'} onClick={() => setMetricFocus('uniqueVisitors')} />
        <KpiCard label="Total Visits"    value={k.totalVisits}    deltaPct={vsPrev.totalVisits}    highlighted={metricFocus==='totalVisits'}    onClick={() => setMetricFocus('totalVisits')} />
        <KpiCard label="Total Pageviews" value={k.pageviews}      deltaPct={vsPrev.pageviews}      highlighted={metricFocus==='pageviews'}      onClick={() => setMetricFocus('pageviews')} />
        <KpiCard label="Views Per Visit" value={k.viewsPerVisit}  deltaPct={vsPrev.viewsPerVisit}  format={(v) => (v != null ? v.toFixed(2) : '—')} />
        <KpiCard label="Bounce Rate"     value={k.bounceRate}     deltaPct={vsPrev.bounceRate}     format={(v) => (v != null ? `${v}%` : '—')} />
        <KpiCard label="Visit Duration"  value={k.avgDuration}    deltaPct={vsPrev.avgDuration}    format={(v) => fmtDuration(v)} />
      </div>

      {/* Time series chart */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
        {seriesData.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 12 }}>暂无数据</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={seriesData}>
              <defs>
                <linearGradient id="vGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={C.accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={C.accent} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} width={28} />
              <Tooltip {...TIP} />
              <Area type="monotone" dataKey="value" stroke={C.accent} strokeWidth={2} fill="url(#vGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Breakdowns: 2x2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <BreakdownPanel
          tabs={[
            { key: 'sources', label: 'Sources', items: data?.sources || [] },
          ]}
        />
        <BreakdownPanel
          title="Top Pages"
          items={(data?.topPages || []).map(p => ({ name: p.page, count: p.count, pct: null }))}
          tabs={[{ key: 'pages', label: 'Top Pages', items: (data?.topPages || []).map(p => ({ name: p.page, count: p.count, pct: null })) }]}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <BreakdownPanel
          tabs={[
            { key: 'countries', label: 'Countries', items: data?.countries || [] },
            { key: 'regions',   label: 'Regions',   items: data?.regions   || [] },
          ]}
        />
        <BreakdownPanel
          tabs={[
            { key: 'browsers', label: 'Browsers', items: data?.browsers || [] },
            { key: 'devices',  label: 'Devices',  items: data?.devices  || [] },
          ]}
        />
      </div>
    </div>
  );
}

function fmtDuration(s) {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
